// components/SimpleVoiceControl.js
import React, { useEffect } from 'react';
import { Bus, Volume2, VolumeX } from 'lucide-react';
import { AVAILABLE_VOICES, VOICE_GROUPS, findVoiceForLanguage } from '../ItineraryCreator/utils/voiceConstants';

const SimpleVoiceControl = ({
  selectedVoice,
  onVoiceChange,
  speechRate = '1',
  onSpeechRateChange,
  contentLanguage = 'en'
}) => {
  // Get default speech rate based on voice type
  const getDefaultSpeechRate = (voiceId) => {
    if (!voiceId) return '1.0';

    // Gemini voices work better with faster speech rate
    if (voiceId.includes('Gemini')) {
      return '1.15';
    }

    // Chirp3 voices also benefit from slightly faster rate
    if (voiceId.includes('Chirp3')) {
      return '1.1';
    }

    // Azure voices
    if (voiceId.includes('Neural') || voiceId.includes('Multilingual')) {
      return '1.0';
    }

    // Default for all other voices
    return '1.0';
  };

  // Check if selected voice is a Gemini voice
  const isGeminiVoice = () => {
    return selectedVoice && selectedVoice.includes('Gemini');
  };

  // Initialize voice based on content language
  useEffect(() => {
    if (!selectedVoice && contentLanguage) {
      const recommendedVoice = findVoiceForLanguage(contentLanguage);
      if (recommendedVoice && onVoiceChange) {
        onVoiceChange(recommendedVoice);
        // Also set the default speech rate for this voice
        if (onSpeechRateChange) {
          onSpeechRateChange(getDefaultSpeechRate(recommendedVoice));
        }
      }
    }
  }, [contentLanguage, selectedVoice, onVoiceChange, onSpeechRateChange]);

  // Enhanced voice info extraction with fallback
  const selectedVoiceInfo = selectedVoice ? AVAILABLE_VOICES[selectedVoice] || {} : {};
  
  // Determine if it's an Azure voice with safer property access
  const isAzureVoice = selectedVoiceInfo.provider === 'Azure';

  // Safer voice name extraction
  const voiceName = selectedVoiceInfo.name || selectedVoice || 'Unknown Voice';

  return (
    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
      <label className="block text-sm font-medium mb-2 flex items-center gap-2">
        <Volume2 className="w-5 h-5 text-blue-600" /> 
        Text-to-Speech Voice
      </label>
      
      {/* Voice selection dropdown */}
      <div className="relative mb-3">
        <Bus className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-500" />
        <select
          value={selectedVoice || ''}
          onChange={(e) => {
            const newVoice = e.target.value;
            console.log('Selected voice:', newVoice);
            onVoiceChange(newVoice);
            // Auto-adjust speech rate when voice changes
            if (onSpeechRateChange) {
              const newRate = getDefaultSpeechRate(newVoice);
              console.log('Auto-adjusting speech rate to:', newRate);
              onSpeechRateChange(newRate);
            }
          }}
          className="w-full rounded border border-blue-300 bg-white py-2 pl-9 pr-2"
        >
          <option value="" disabled>Select a voice</option>
          
          {/* Organized by language groups */}
          {VOICE_GROUPS.map(group => (
            <optgroup key={group.language} label={group.language}>
              {group.voices.map(voiceId => {
                const voice = AVAILABLE_VOICES[voiceId];
                return (
                  <option key={voiceId} value={voiceId}>
                    {voice?.name || voiceId}
                  </option>
                );
              })}
            </optgroup>
          ))}
        </select>
      </div>
      
      {/* Speech rate slider */}
      <div className="mb-1">
        <label className="flex justify-between items-center text-sm mb-1">
          <span className="font-medium flex items-center gap-1">
            <VolumeX className="w-4 h-4 text-blue-600" /> Speech Rate
            {isGeminiVoice() && <span className="ml-1 text-xs text-blue-600">(Optimized for Gemini)</span>}
          </span>
          <span className="text-xs font-medium">{speechRate}x</span>
        </label>

        <input
          type="range"
          min="0.5"
          max="2.0"
          step="0.05"
          value={speechRate}
          onChange={(e) => onSpeechRateChange(e.target.value)}
          disabled={isGeminiVoice()}
          className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        />

        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>0.5x</span>
          <span>1.0x</span>
          <span>2.0x</span>
        </div>

        {isGeminiVoice() ? (
          <div className="text-xs text-amber-700 mt-1 bg-amber-50 p-2 rounded border border-amber-300">
            <p className="font-medium mb-1">⚠️ Speech rate control is not available for Gemini voices</p>
            <p className="text-amber-600">
              To make Gemini speak faster, use the <strong>Pace slider</strong> in the purple "Gemini Voice Style Controls" section below. Set it to <strong>70-100</strong> for fast speech.
            </p>
          </div>
        ) : (
          <p className="text-xs text-gray-600 mt-1">
            Adjust the playback speed of the generated audio
          </p>
        )}
      </div>
       
      {/* Currently selected voice info */}
      <div className="text-xs text-blue-600 mt-2 bg-white p-2 rounded border border-blue-100">
        <p><strong>Selected:</strong> {voiceName}</p>
        <p><strong>Provider:</strong> {selectedVoiceInfo.provider || 'Unknown'}</p>
        {isAzureVoice && (
          <>
            <p><strong>Azure Voice Name:</strong> {selectedVoiceInfo.ttsRequest?.azureVoiceName || 'N/A'}</p>
            <p><strong>Language:</strong> {selectedVoiceInfo.ttsRequest?.azureLanguage || 'N/A'}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default SimpleVoiceControl;
